import { BuildingOffice2Icon, BuildingOfficeIcon, DocumentCheckIcon, SquaresPlusIcon } from '@heroicons/react/20/solid';
import { formatMixedCase } from '../../utilities/formatting';
import { Entity } from '../../types/entity';
import { ButtonV2, TooltipV2 } from '@cimplico/components';
import { ArchiveBoxIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useRef, useState } from 'react';
import { SubscriptionTier } from '../../types/firm';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { disableCreateWorkpaperHint, hasRemainingWorkpapers } from '../new-workpaper/utils';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import EntitiesArchiveConfirm from '../entities/EntitiesArchiveConfirm';
import EntitiesUnarchiveConfirm from '../entities/EntitiesUnarchiveConfirm';
import plural from '../../utilities/plural';
import useIsClamped from '../../hooks/use-line-clamp-active';
import PendingDeactivationBadge, { ActiveBadge } from './BillingStatusBadge';
import DeactivateButton from './DeactivateButton';

type EntityDetailsPanelProps = {
    entity: Entity;
};

function EntityDetailsPanel({ entity }: EntityDetailsPanelProps): JSX.Element {
    const firm = useFirmContext();
    const { shortId, subscription, subscriptionVersion } = firm;
    const { isExternalUser } = useIsFirmRole();
    const [action, setAction] = useState<string | undefined>();

    const navigate = useNavigate();
    const activeSubscription = useIsActiveSubscription();

    const disableCreateHint = disableCreateWorkpaperHint(firm, entity, subscription, isExternalUser);

    function handleNewWorkpaper(): void {
        if (subscriptionVersion === 1 && !hasRemainingWorkpapers(entity)) {
            setAction('UPGRADE');
            return;
        }

        navigate(`/${shortId}/workpapers/new?entityId=${entity.id}&returnTo=/${shortId}/entities/${entity.id}?tab=workpapers`);
    }

    return (
        <>
            {action === 'UPGRADE' && (
                <UpgradePrompt
                    targetTier={SubscriptionTier.ESSENTIAL}
                    onClose={(): void => setAction(undefined)}
                    quota={!hasRemainingWorkpapers}
                />
            )}
            <EntitiesArchiveConfirm
                entities={[entity]}
                isOpen={action === 'ARCHIVE'}
                onClose={(): void => setAction(undefined)}
                onArchive={(): void => setAction(undefined)}
            />
            <EntitiesUnarchiveConfirm
                entities={[entity]}
                isOpen={action === 'UNARCHIVE'}
                onClose={(): void => setAction(undefined)}
                onUnarchive={(): void => setAction(undefined)}
            />
            <div className="border border-gray-200 dark:border-white/[0.07] rounded-lg w-full overflow-hidden py-4 px-8 grid gap-y-2 bg-white dark:bg-gray-800">
                <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                        <h1 className="text-gray-800 dark:text-gray-200 font-bold text-2xl">{entity.name}</h1>
                        {entity.subscriptionActive && !entity.flaggedDeactivatedAt && <ActiveBadge />}
                        {entity.subscriptionActive && entity.flaggedDeactivatedAt && <PendingDeactivationBadge />}
                    </div>

                    <div className="flex gap-4">
                        {entity.subscriptionActive && (
                            <div className="shrink-0">
                                <DeactivateButton entity={entity} />
                            </div>
                        )}
                        {entity.isArchived && (
                            <div className="shrink-0">
                                <ButtonV2
                                    size="xs"
                                    type="secondary"
                                    label="Unarchive"
                                    onClick={(): void => setAction('UNARCHIVE')}
                                    isDisabled={!activeSubscription || isExternalUser}
                                />
                            </div>
                        )}
                        {!entity.isArchived && (
                            <div className="shrink-0">
                                <ButtonV2
                                    size="xs"
                                    type="danger"
                                    label="Archive"
                                    leftIcon={<ArchiveBoxIcon />}
                                    onClick={(): void => setAction('ARCHIVE')}
                                    isDisabled={!activeSubscription || entity.activeWorkpapers.length !== 0 || isExternalUser}
                                    disabledHint={
                                        entity.activeWorkpapers.length > 0
                                            ? 'This entity has open workpapers and cannot be archived.'
                                            : undefined
                                    }
                                    tooltipPosition="left"
                                />
                            </div>
                        )}
                        <div className="shrink-0">
                            <ButtonV2
                                size="xs"
                                label="New Workpaper"
                                leftIcon={<PlusIcon />}
                                onClick={handleNewWorkpaper}
                                disabledHint={disableCreateHint}
                                isDisabled={!!disableCreateHint}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex gap-4 items-center text-gray-500 dark:text-gray-400">
                    <div className="flex gap-1 items-center text-sm">
                        <BuildingOfficeIcon className="w-4 h-4" />
                        <p>{formatMixedCase(entity.type)}</p>
                    </div>
                    {entity.groups.length > 0 && <GroupLink groupName={entity.groups[0].name} groupId={entity.groups[0].id} />}
                    <div className="flex gap-1 items-center text-sm">
                        <DocumentCheckIcon className="w-4 h-4" />
                        <p>
                            {entity.activeWorkpapers.length} open {plural(entity.activeWorkpapers.length, 'workpaper', 'workpapers')}
                        </p>
                    </div>
                    <div className="flex gap-1 items-center text-sm">
                        <SquaresPlusIcon className="w-4 h-4" />
                        <p>
                            {Object.values(entity.integrations).filter((val) => val !== null).length}{' '}
                            {plural(Object.values(entity.integrations).filter((val) => val !== null).length, 'integration', 'integrations')}
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}

type GroupLinkProps = {
    groupName: string;
    groupId: string;
};
/**
 * Links off to the entity's group overview.
 *
 * @param GroupLinkProps
 * @returns
 */
function GroupLink({ groupId, groupName }: GroupLinkProps): JSX.Element {
    const groupNameRef = useRef<HTMLDivElement | null>(null);
    const isClamping = useIsClamped(groupNameRef);

    return (
        <div className="flex gap-1 items-center text-sm w-fit">
            <BuildingOffice2Icon className="w-4 h-4" />
            <TooltipV2 message={groupName} delayMs={750} disabled={!isClamping}>
                <div className="max-w-[40ch] w-fit line-clamp-1 break-all text-indigo-600" ref={groupNameRef}>
                    <Link className="font-medium w-fit" to={`../../groups/${groupId}`}>
                        {groupName}
                    </Link>
                </div>
            </TooltipV2>
        </div>
    );
}

export default EntityDetailsPanel;
